/**
 * Monaco Collaboration Manager
 * 
 * Manages cursor decorations and selections for remote collaborators in Monaco editor
 * Provides Live Share-style visual feedback for multi-user editing
 */

class MonacoCollaborationManager {
  constructor(editor, monaco) {
    this.editor = editor;
    this.monaco = monaco;
    
    // Track remote cursors and decorations
    this.remoteCursors = new Map(); // userId -> { decorations, widgets, color, username }
    this.decorationIds = new Map(); // userId -> array of decoration IDs
    this.widgetIds = new Map(); // userId -> widget instance
  }

  /**
   * Update or create remote cursor decoration
   * @param {string} userId - Remote user ID
   * @param {string} username - Display username
   * @param {string} color - User color (hex)
   * @param {object} position - Cursor position {line, column}
   */
  updateCursor(userId, username, color, position) {
    if (!position || typeof position.line !== 'number' || typeof position.column !== 'number') {
      console.warn('[Collaboration] Invalid cursor position:', position);
      return;
    }

    // Remove old decorations
    this.removeCursor(userId);

    const { line, column } = position;
    
    // Create cursor decoration (thin vertical line)
    const cursorDecorations = [
      {
        range: new this.monaco.Range(line, column, line, column),
        options: {
          className: 'remote-cursor',
          stickiness: this.monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges,
          beforeContentClassName: 'remote-cursor-line',
          before: {
            content: '',
            inlineClassName: `remote-cursor-marker`,
            inlineClassNameAffectsLetterSpacing: false,
          },
          // Custom CSS for cursor line
          glyphMarginClassName: 'remote-cursor-glyph'
        }
      }
    ];

    // Apply decorations
    const decorationIds = this.editor.deltaDecorations([], cursorDecorations);

    // Create username label widget
    const widget = this.createCursorWidget(userId, username, color, line, column);

    // Store references
    this.decorationIds.set(userId, decorationIds);
    this.widgetIds.set(userId, widget);
    this.remoteCursors.set(userId, {
      decorations: cursorDecorations,
      color,
      username,
      position,
      lastUpdate: Date.now(),
    });

    // Inject CSS for this user's cursor color
    this.injectCursorStyles(userId, color);
  }

  /**
   * Create cursor label widget
   */
  createCursorWidget(userId, username, color, line, column) {
    const domNode = document.createElement('div');
    domNode.className = 'remote-cursor-widget';
    domNode.style.backgroundColor = color;
    domNode.style.color = this.getContrastColor(color);
    domNode.textContent = username;
    domNode.setAttribute('data-user-id', userId);

    const widget = {
      domNode,
      getId: () => `remote-cursor-widget-${userId}`,
      getDomNode: () => domNode,
      getPosition: () => ({
        position: {
          lineNumber: line,
          column: column
        },
        preference: [
          this.monaco.editor.ContentWidgetPositionPreference.ABOVE,
          this.monaco.editor.ContentWidgetPositionPreference.BELOW
        ]
      })
    };

    this.editor.addContentWidget(widget);
    return widget;
  }

  /**
   * Remove cursor for user
   */
  removeCursor(userId) {
    // Remove decorations
    const decorationIds = this.decorationIds.get(userId);
    if (decorationIds) {
      this.editor.deltaDecorations(decorationIds, []);
      this.decorationIds.delete(userId);
    }

    // Remove widget
    const widget = this.widgetIds.get(userId);
    if (widget) {
      this.editor.removeContentWidget(widget);
      this.widgetIds.delete(userId);
    }

    // Remove cursor data
    this.remoteCursors.delete(userId);
    
    // Remove injected styles
    this.removeInjectedStyles(userId);
  }

  /**
   * Remove all remote cursors
   */
  clearAllCursors() {
    for (const userId of this.remoteCursors.keys()) {
      this.removeCursor(userId);
    }
  }

  /**
   * Inject CSS styles for cursor color
   */
  injectCursorStyles(userId, color) {
    const styleId = `remote-cursor-style-${userId}`;
    
    // Remove old style if exists
    const oldStyle = document.getElementById(styleId);
    if (oldStyle) {
      oldStyle.remove();
    }

    // Create new style
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .remote-cursor-line[data-user-id="${userId}"]::before {
        content: '';
        position: absolute;
        width: 2px;
        height: 100%;
        background-color: ${color};
        animation: cursor-blink 1s infinite;
      }
      
      @keyframes cursor-blink {
        0%, 49% { opacity: 1; }
        50%, 100% { opacity: 0.3; }
      }
    `;
    
    document.head.appendChild(style);
  }

  /**
   * Remove injected cursor styles
   */
  removeInjectedStyles(userId) {
    const styleId = `remote-cursor-style-${userId}`;
    const style = document.getElementById(styleId);
    if (style) {
      style.remove();
    }
  }

  /**
   * Get contrasting text color for background
   */
  getContrastColor(hexColor) {
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return white for dark colors, black for light colors
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }

  /**
   * Get all active remote cursors
   */
  getActiveCursors() {
    return Array.from(this.remoteCursors.entries()).map(([userId, data]) => ({
      userId,
      ...data
    }));
  }

  /**
   * Clean up stale cursors (no update in 30 seconds)
   */
  cleanupStaleCursors(maxAge = 30000) {
    const now = Date.now();
    
    for (const [userId, data] of this.remoteCursors.entries()) {
      if (now - data.lastUpdate > maxAge) {
        console.log(`[Collaboration] Removing stale cursor for user ${userId}`);
        this.removeCursor(userId);
      }
    }
  }

  /**
   * Dispose and clean up all resources
   */
  dispose() {
    this.clearAllCursors();
    this.decorationIds.clear();
    this.widgetIds.clear();
    this.remoteCursors.clear();
  }
}

export default MonacoCollaborationManager;
